/* File: sidebar.jsx
 * Path: ananunadm/src/components/main/sidebar.jsx
 */
// sidebar.jsx - Optimized version with AI conversations moved to right sidebar

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import config from "../../config";
import { useUser } from "../../helpers/userContext";
import { useLang } from "../../helpers/language";
import NProgress from "nprogress";
import ApiClient, * as API from "../../helpers/api";
import { useViewportSize } from "@mantine/hooks";
import { T } from "../../helpers/translator";
import { getEnabledModules } from "../../EnvironmentBasedRouter";
import {
	Text,
	Modal,
	Button,
	UnstyledButton,
	Avatar,
	Image,
	NavLink,
	ScrollArea,
	Badge,
	TextInput,
	Stack,
	Group,
	ActionIcon,
	Menu,
	MultiSelect,
	Select,
	Switch,
	Paper,
	Box,
	Divider,
	useMantineTheme,
	Accordion,
	Collapse,
	Portal
} from "@mantine/core";
import {
	IconFolder,
	IconFolderOpen,
	IconFolderSymlink,
	IconHistory,
	IconStar,
	IconSearch,
	IconShield,
	IconTrash,
	IconUsersPlus,
	IconX,
	IconChevronDown,
	IconPin,
	IconPinnedOff,
	IconBell,
	IconBellOff,
	IconBan,
	IconEraser,
	IconArchive,
	IconArchiveOff,
	IconMailOpened,
	IconMail,
	IconClock,
	IconChevronRight,
	IconCalendarPlus
} from "@tabler/icons-react";
import { useSocket } from "../../helpers/socket";
import { useActivePlatform } from "../../helpers/activePlatform";
import ChatNavLink from "./chatNavLink";
import { useGlobal } from "@/helpers/global";
import classes from "./sidebar.module.css";
import { filterMenuItemsByReadPermission } from "../../helpers/platformMenuAccess";
import { withoutPageSettingsItems } from "../../helpers/pageSettingsNavigation";
import { useSecurityAlertsBadge } from "../../helpers/securityAlertsBadgeContext";
import {
	buildChatRoute,
	buildCreateChatUserOptions,
	hasDirectChatWithUser,
	filterUserSearchResults,
	filterSidebarChats,
	getDirectChatPartner,
	getLocalizedText,
	resolveDirectChatForUser,
	partitionSidebarChats,
	groupChatsByType,
	getAllSidebarChats,
	getSidebarUnreadCount,
	isChatMuted
} from "./sidebarChatUtils";
import { getChatFeatureAccess } from "../../helpers/chatPermissionUtils";
import { CHAT_AVATAR_UPDATED_EVENT } from "../../helpers/ticketMenuReload";
import { getUserImageSrc } from "../../helpers/uploadImage";
import { isModuleEnabled } from "../../helpers/menuFilter";
import {
	CHAT_GLOBAL_SETTINGS_UPDATED_EVENT,
	isSidebarCompactModeEnabled,
	isSidebarLastMessageEnabled
} from "../../helpers/chatSettings";

const CHAT_NOTIFICATION_LANGS = ["en", "ru", "hy", "tr", "ar", "zh", "fa", "he", "fr", "es", "pt"];
const filesBase = config.api.files?.root || "files";
const filesBrowserBasePath = "/files/browser";
const filesFolderIconColor = "#f5b942";
const SIDEBAR_GROUPS_STORAGE_KEY = "apit.sidebar.expanded-groups.v1";

function readExpandedMenuGroups() {
	if (typeof window === "undefined") return {};
	try {
		const parsed = JSON.parse(window.localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY) || "{}");
		return parsed && typeof parsed === "object" ? parsed : {};
	} catch (_) {
		return {};
	}
}

const fileSidebarTopSections = [
	{ label: "Recent", section: "recent" },
	{ label: "Favorites", section: "favorites" },
	{ label: "Shared with me", section: "shared-with-me" }
];

const fileSidebarBottomSections = [
	{ label: "All Folders", section: "apit-folders" },
	{ label: "My Files", section: "my-files" },
	{ label: "Trash", section: "trash" }
];

function menuRoutePathOnly(routeStr) {
	return (routeStr || "").split("?")[0];
}

/** Convert a #rgb/#rrggbb module color into an rgba() string with the given alpha. */
function hexToRgba(hex, alpha = 1) {
	if (!hex || typeof hex !== "string") return null;
	let h = hex.trim().replace(/^#/, "");
	if (h.length === 3) {
		h = h
			.split("")
			.map((c) => c + c)
			.join("");
	}
	if (h.length !== 6) return null;
	const num = Number.parseInt(h, 16);
	if (Number.isNaN(num)) return null;
	const r = (num >> 16) & 255;
	const g = (num >> 8) & 255;
	const b = num & 255;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toMenuLabel(name, lang) {
	if (typeof name === "string") {
		return name.charAt(0).toUpperCase() + name.slice(1);
	}
	if (!name || typeof name !== "object") return "";
	const raw =
		name?.[lang] ??
		name?.en ??
		name?.ru ??
		name?.hy ??
		Object.values(name).find((v) => typeof v === "string" && v.trim());
	if (typeof raw !== "string") return "";
	return raw.charAt(0).toUpperCase() + raw.slice(1);
}

const buildLocalizedNotificationMap = (resolver) =>
	CHAT_NOTIFICATION_LANGS.reduce((accumulator, currentLang) => {
		accumulator[currentLang] = resolver(currentLang);
		return accumulator;
	}, {});

/** Match list routes that include query variants (e.g. ticket list all / mine / domain / type). */
function isMenuRouteActive(location, routeStr) {
	const pathPart = menuRoutePathOnly(routeStr);
	const normPath = location.pathname.replace(/\/$/, "") || "/";
	const expected = "/" + pathPart.replace(/^\//, "");
	if (normPath !== expected) return false;
	const qIdx = routeStr.indexOf("?");
	if (qIdx === -1) {
		const loc = new URLSearchParams(location.search);
		return !loc.get("domain") && loc.get("mine") !== "true";
	}
	const want = new URLSearchParams(routeStr.slice(qIdx + 1));
	const loc = new URLSearchParams(location.search);
	for (const [k, v] of want.entries()) {
		if (loc.get(k) !== v) return false;
	}
	// Domain "All" link has ?domain= only; do not highlight it when a type filter is active.
	if (!want.has("type") && loc.get("type")) return false;
	return true;
}

function getMenuIconSrc(route, icon) {
	const assetPath =
		menuRoutePathOnly(route) === "security/cameras"
			? "/assets/security-camera-svgrepo-com.svg"
			: `/assets/${icon}`;
	const base = config.api.MAIN_APP_URL;
	return base ? `${base}${assetPath}` : assetPath;
}

function resolveLocalized(value, fallback = "") {
	if (!value && value !== 0) return fallback;
	if (typeof value === "string") return value || fallback;
	if (typeof value === "object" && !Array.isArray(value)) {
		const picked =
			value.en ||
			value.hy ||
			value.ru ||
			value.tr ||
			value.fr ||
			value.es ||
			value.pt ||
			value.ar ||
			value.zh ||
			value.fa ||
			value.he;
		if (picked && typeof picked === "string") return picked;
		const any = Object.values(value).find((v) => v && typeof v === "string");
		return any || fallback;
	}
	return String(value) || fallback;
}

function entityName(entity, fallback = "") {
	return resolveLocalized(entity?.name, entity?.virtualRoot || fallback);
}

function encodeFilePathSegment(value = "") {
	return String(value || "")
		.split("/")
		.filter(Boolean)
		.map((segment) => encodeURIComponent(segment))
		.join("/");
}

function filesBrowserUrl({ targetType, targetId, path = "" }) {
	const encodedPath = encodeFilePathSegment(path);
	return `${filesBrowserBasePath}/${targetType}/${targetId}${encodedPath ? `/${encodedPath}` : ""}`;
}

function filesSectionUrl(section) {
	return `${filesBrowserBasePath}/section/${encodeURIComponent(section)}`;
}

function isAdminUser(user) {
	const values = [
		user?.role,
		user?.role?.name,
		user?.roleName,
		user?.type,
		user?.userType,
		user?.isAdmin
	].map((value) => String(value || "").toLowerCase());
	return values.some(
		(value) =>
			value === "admin" || value === "superadmin" || value === "administrator" || value === "true"
	);
}

export default function Sidebar({ onNavigate, onAiAssistantOpen }) {
	const { user } = useUser();
	const { lang } = useLang();
	const history = useHistory();
	const location = useLocation();
	const [createGroupOpened, setCreateGroupOpened] = useState(false);
	const [chatSearchQuery, setChatSearchQuery] = useState("");
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [isPrivateChat, setIsPrivateChat] = useState(false);
	const [createChatType, setCreateChatType] = useState("group");
	const [isCreatingChat, setIsCreatingChat] = useState(false);
	const creatingChatRef = useRef(false);
	const [existingDirectChats, setExistingDirectChats] = useState([]);
	const [chatContactsLoading, setChatContactsLoading] = useState(false);
	const [groupName, setGroupName] = useState("");
	const [availableUsers, setAvailableUsers] = useState([]);
	const { height } = useViewportSize();
	const { socket, connectedUsers, createNotification } = useSocket();
	const {
		activePlatform,
		setActivePlatform,
		activePage,
		setActivePage,
		subActivePage,
		setSubActivePage
	} = useActivePlatform();
	const theme = useMantineTheme();
	const isDark = theme.colorScheme === "dark";
	const { platforms, filteredMenu, currentPlatformIndex } = useGlobal();
	const { badgeRefreshToken } = useSecurityAlertsBadge();
	const [expandedMenuGroups, setExpandedMenuGroups] = useState(readExpandedMenuGroups);
	const [officeBadgeCounts, setOfficeBadgeCounts] = useState({});

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(expandedMenuGroups));
		}
	}, [expandedMenuGroups]);

	// Reveal the active group and close its siblings. This also keeps the
	// persisted state in the same one-open-group shape used by click handling.
	useEffect(() => {
		setExpandedMenuGroups((current) => {
			let changed = false;
			const next = { ...current };
			(platforms || []).forEach((platform) => {
				const platformPrefix = `${platform.id || platform.route}:`;
				let activeKey = null;
				(platform.menu || []).forEach((item) => {
					if (!item.subMenu?.length) return;
					const active = item.subMenu.some((subItem) =>
						location.pathname.startsWith(`/${menuRoutePathOnly(subItem.route)}`)
					);
					if (active) activeKey = `${platformPrefix}${item.id || item.name?.en}`;
				});
				if (!activeKey) return;
				Object.keys(next).forEach((key) => {
					if (key.startsWith(platformPrefix) && key !== activeKey && next[key] !== false) {
						next[key] = false;
						changed = true;
					}
				});
				if (next[activeKey] !== true) {
					next[activeKey] = true;
					changed = true;
				}
			});
			return changed ? next : current;
		});
	}, [location.pathname, platforms]);

	// Memoized route checks
	const isChatRoute = useMemo(() => location.pathname.startsWith("/chat"), [location.pathname]);

	// Get enabled modules from environment - memoized
	const enabledModules = useMemo(() => getEnabledModules(), []);

	/** Invert /assets PNGs inside expanded sections only (not platform row icons). */
	const assetMenuIconStyle = useMemo(() => (isDark ? { filter: "invert(0)" } : {}), [isDark]);

	const [firstTimeLoaded, setFirstTimeLoaded] = useState(false);
	const [isBusy, setBusy] = useState(true);
	/** Count of security alerts with status open (Security → Alerts nav badge). */
	const [securityOpenAlertsCount, setSecurityOpenAlertsCount] = useState(null);
	const [chats, setChats] = useState([]);
	const [directChats, setDirectChats] = useState([]);
	const [channelChats, setChannelChats] = useState([]);
	const [groupChats, setGroupChats] = useState([]);
	const [pinnedChats, setPinnedChats] = useState([]);
	const [archivedChats, setArchivedChats] = useState([]);
	const [blockedChats, setBlockedChats] = useState([]);
	const [showSidebarLastMessage, setShowSidebarLastMessage] = useState(true);
	const [sidebarCompactMode, setSidebarCompactMode] = useState(false);
	const allChats = useMemo(() => chats, [chats]);
	const canClearHistory = useMemo(
		() => getChatFeatureAccess(user, "clearHistory").update,
		[user]
	);
	const canDeleteChat = useMemo(
		() => getChatFeatureAccess(user, "deleteChat").update,
		[user]
	);
	const archivedByType = useMemo(() => groupChatsByType(archivedChats), [archivedChats]);
	const allSidebarChats = useMemo(() => getAllSidebarChats(chats, user?._id), [chats, user?._id]);
	const chatUnreadCounts = useMemo(() => ({
		all: getSidebarUnreadCount(allSidebarChats),
		pinned: getSidebarUnreadCount(allSidebarChats.filter((chat) => chat.userState?.pinned)),
		direct: getSidebarUnreadCount(allSidebarChats.filter((chat) => chat.chatType === "direct")),
		channel: getSidebarUnreadCount(allSidebarChats.filter((chat) => chat.chatType === "channel")),
		group: getSidebarUnreadCount(allSidebarChats.filter((chat) => chat.chatType !== "direct" && chat.chatType !== "channel")),
		archived: getSidebarUnreadCount(archivedChats),
		blocked: getSidebarUnreadCount(blockedChats)
	}), [allSidebarChats, archivedChats, blockedChats]);
	const chatSectionLabel = (title, count) => (
		<Group spacing={6} noWrap>
			<Text component="span" inherit>{title}</Text>
			{count > 0 && <Badge size="sm" variant="filled">{count > 99 ? "99+" : count}</Badge>}
		</Group>
	);
	const filteredChats = useMemo(
		() => filterSidebarChats(allChats, chatSearchQuery, user?._id, lang),
		[allChats, chatSearchQuery, user?._id, lang]
	);
	const hasChatSearchQuery = Boolean(chatSearchQuery.trim());
	const [chatUserSearchResults, setChatUserSearchResults] = useState([]);
	const [chatContextMenu, setChatContextMenu] = useState(null);
	// Last anchor coords — kept stable so the closing menu doesn't snap to (0,0).
	const chatMenuPosRef = useRef({ x: 0, y: 0 });
	const [sidebarMeetings, setSidebarMeetings] = useState([]);
	const [fileMounts, setFileMounts] = useState([]);
	const [fileProviders, setFileProviders] = useState([]);
	const [filesBasicsLoaded, setFilesBasicsLoaded] = useState(false);
	const createChatUserOptions = useMemo(
		() => buildCreateChatUserOptions(availableUsers, user?._id, lang, T("unknown", lang))
			.filter((option) => createChatType !== "direct" ||
				!hasDirectChatWithUser([...allChats, ...existingDirectChats], user?._id, option.value)),
		[availableUsers, user?._id, lang, createChatType, allChats, existingDirectChats]
	);
	const createChatUserLabelsById = useMemo(
		() => new Map(createChatUserOptions.map((option) => [option.value, option.label])),
		[createChatUserOptions]
	);

	// Collapsible sections state — direct messages open when chat sidebar is shown
	const [channelsOpen, setChannelsOpen] = useState(false);
	const [groupChatsOpen, setGroupChatsOpen] = useState(false);
	const [directMessagesOpen, setDirectMessagesOpen] = useState(true);
	const [meetingsOpen, setMeetingsOpen] = useState(false);
	const [pinnedOpen, setPinnedOpen] = useState(true);
	const [allChatsOpen, setAllChatsOpen] = useState(true);
	const [archivedOpen, setArchivedOpen] = useState(false);
	const [blockedOpen, setBlockedOpen] = useState(false);

	// Accordion state - derived from URL
	const [accordionValue, setAccordionValue] = useState(null);

	const [{ activeTime = 0, inactiveTime = 0 }, setTimeLapse] = useState(() => {
		const today = new Date().toISOString().split("T")[0];
		const todayRecord = user?.timeLapse?.find((item) => item?.date?.split("T")[0] === today);
		return todayRecord || { activeTime: 0, inactiveTime: 0 };
	});

	// Function to find platform index from current URL path
	const findPlatformIndexFromUrl = useCallback(
		(pathname) => {
			if (!platforms?.length) return null;

			// Remove leading slash and get the first segment
			const pathSegments = pathname.replace(/^\//, "").split("/");
			const firstSegment = pathSegments[0];

			if (!firstSegment) return null;

			// Find platform that matches the route
			const index = platforms.findIndex((platform) => {
				// Direct route match
				if (platform.route === firstSegment) return true;

				// Check if any menu item route starts with this path
				return platform.menu?.some((menuItem) => {
					const menuRoute = menuItem.route?.split("/")[0];
					if (menuRoute === firstSegment) return true;

					// Check submenus
					return menuItem.subMenu?.some((subItem) => {
						const subRoute = subItem.route?.split("/")[0];
						return subRoute === firstSegment;
					});
				});
			});

			return index >= 0 ? `${index}` : null;
		},
		[platforms]
	);

	// Update accordion value when URL changes
	useEffect(() => {
		const platformIndex = findPlatformIndexFromUrl(location.pathname);
		if (platformIndex !== null) {
			setAccordionValue(platformIndex);
		}
	}, [location.pathname, findPlatformIndexFromUrl]);

	const securityBadgePathKey = location.pathname.startsWith("/security") ? location.pathname : "";
	useEffect(() => {
		if (!user?._id || !isModuleEnabled("security")) return;
		let cancelled = false;
		(async () => {
			try {
				const res = await API.get({
					route: config.api.security.alerts,
					query: { limit: 1, skip: 0, status: "open" }
				});
				if (!cancelled) setSecurityOpenAlertsCount(Number(res.total) || 0);
			} catch {
				if (!cancelled) setSecurityOpenAlertsCount(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [user?._id, securityBadgePathKey, badgeRefreshToken]);

	const officeBadgePathKey = location.pathname.startsWith("/office") ? location.pathname : "";
	useEffect(() => {
		if (!user?._id || !(platforms || []).some((platform) => platform.route === "office")) return;
		let cancelled = false;
		(async () => {
			try {
				const counts = await API.get({ route: config.api.office.dashboard });
				if (!cancelled) setOfficeBadgeCounts(counts && typeof counts === "object" ? counts : {});
			} catch {
				if (!cancelled) setOfficeBadgeCounts({});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [user?._id, officeBadgePathKey, platforms]);

	// Also update when currentPlatformIndex changes from global state
	useEffect(() => {
		if (currentPlatformIndex !== undefined && currentPlatformIndex >= 0) {
			setAccordionValue(`${currentPlatformIndex}`);
		}
	}, [currentPlatformIndex]);

	// Submenu items: RBAC read; user.ananunaki skips checks (platformMenuAccess.js)
	const getFilteredPlatformMenu = useCallback(
		(platform) => {
			if (!platform?.menu) return [];
			return withoutPageSettingsItems(filterMenuItemsByReadPermission(platform.menu, user));
		},
		[user]
	);

	// API calls - memoized with useCallback
	const getChats = useCallback(async () => {
		try {
			const chats = await API.get({
				route: config.api.chat.chatsWithMessages,
				query: { userId: user._id }
			});
			if (chats?.length) {
				setChats(chats); // Avatars must not delay the conversation list.
				const groupLikeChats = chats.filter(
					(chat) =>
						chat?.chatType !== "direct" && chat?.uploads === undefined
				);
				const directChatsNeedingUserUploads = chats.filter(
					(chat) =>
						chat?.chatType === "direct" &&
						Array.isArray(chat?.users) &&
						chat.users.some(
							(participant) =>
								participant?.uploads === undefined
						)
				);

				if (!groupLikeChats.length && !directChatsNeedingUserUploads.length) {
					setChats(chats);
					return;
				}

				try {
					const groupChatIds = groupLikeChats.map((chat) => chat?._id).filter(Boolean);
					const directUserIds = [
						...new Set(
							directChatsNeedingUserUploads.flatMap((chat) =>
								(chat.users || [])
									.map((participant) => String(participant?._id || ""))
									.filter(Boolean)
							)
						)
					];

					const [chatUploads, userUploads] = await Promise.all([
						groupChatIds.length
							? API.get({
									route: config.api.system.upload,
									query: {
										obj_id: { $in: groupChatIds },
										destiny: "image"
									}
								})
							: Promise.resolve([]),
						directUserIds.length
							? API.get({
									route: config.api.system.upload,
									query: {
										obj_id: { $in: directUserIds },
										destiny: "image"
									}
								})
							: Promise.resolve([])
					]);

					const reduceUploads = (rows) =>
						(Array.isArray(rows) ? rows : []).reduce((acc, upload) => {
							const key = String(upload?.obj_id || "");
							if (!key) return acc;
							if (!acc[key]) acc[key] = [];
							acc[key].push(upload);
							return acc;
						}, {});

					const uploadsByChatId = reduceUploads(chatUploads);
					const uploadsByUserId = reduceUploads(userUploads);

					const enrichedChats = chats.map((chat) => {
						let nextChat = chat;

						if (chat?.chatType === "direct" && Array.isArray(chat?.users)) {
							const usersWithUploads = chat.users.map((participant) => {
								const userKey = String(participant?._id || "");
								const resolvedUploads = uploadsByUserId[userKey];
								if (!Array.isArray(resolvedUploads) || !resolvedUploads.length) {
									return participant;
								}
								return { ...participant, uploads: resolvedUploads };
							});
							nextChat = { ...nextChat, users: usersWithUploads };
						}

						const uploadKey = String(chat?._id || "");
						const resolvedChatUploads = uploadsByChatId[uploadKey];
						if (Array.isArray(resolvedChatUploads) && resolvedChatUploads.length) {
							nextChat = { ...nextChat, uploads: resolvedChatUploads };
						}

						return nextChat;
					});

					setChats((current) => current.map((chat) => {
						const enriched = enrichedChats.find((item) => item._id === chat._id);
						if (!enriched) return chat;
						return { ...chat, uploads: enriched.uploads, users: (chat.users || []).map((member) => {
							const detail = enriched.users?.find((item) => item._id === member._id);
							return detail ? { ...member, uploads: detail.uploads } : member;
						}) };
					}));
				} catch (uploadError) {
					console.error("Failed to enrich chats with uploaded images:", uploadError);
				}
			} else {
				setChats([]);
			}
		} catch (error) {
			console.error("Failed to fetch chats:", error);
		}
	}, [user._id]);

	const getChatSidebarSettings = useCallback(async () => {
		try {
			const settings = await API.get({ route: config.api.chat.globalSettings });
			setShowSidebarLastMessage(isSidebarLastMessageEnabled(settings));
			setSidebarCompactMode(isSidebarCompactModeEnabled(settings));
		} catch (error) {
			console.error("Failed to fetch chat sidebar settings:", error);
			setShowSidebarLastMessage(true);
			setSidebarCompactMode(false);
		}
	}, []);

	const getUsers = useCallback(async () => {
		try {
			const usersData = await API.get({ route: config.api.hr.user, uploadType: true });
			if (usersData) setAvailableUsers(usersData);
		} catch (error) {
			console.error("Error fetching users:", error);
		}
	}, []);

	useEffect(() => {
		const query = chatSearchQuery.trim();
		if (!query || !user?._id) {
			setChatUserSearchResults([]);
			return undefined;
		}

		const timer = setTimeout(async () => {
			try {
				const regexQuery = { $regex: query, $options: "i" };
				const usersData = await API.get({
					route: config.api.chat.users,
					query: {
						_id: { $ne: user._id },
						$or: [
							{ username: regexQuery },
							{ "name.en": regexQuery },
							{ "name.ru": regexQuery },
							{ "name.hy": regexQuery }
						]
					},
					limit: 50
				});
				setChatUserSearchResults(
					filterUserSearchResults(usersData, query, user._id, allChats, lang)
				);
			} catch (error) {
				console.error("Failed to search chat users:", error);
				setChatUserSearchResults([]);
			}
		}, 250);

		return () => clearTimeout(timer);
	}, [chatSearchQuery, user?._id, allChats, lang]);

	const getMeetings = useCallback(async () => {
		try {
			const meetings = await API.get({
				route: config.api.meetings,
				sort: { startDate: -1 },
				limit: 10
			});
			if (Array.isArray(meetings)) setSidebarMeetings(meetings);
		} catch (error) {
			console.error("Failed to fetch meetings:", error);
		}
	}, []);

	const navigateFromSidebar = useCallback(
		(path) => {
			const normalized = String(path || "").replace(/^\//, "");
			if ((normalized === "ai" || normalized.startsWith("ai/")) && onAiAssistantOpen) {
				onAiAssistantOpen();
				onNavigate?.();
				return;
			}
			history.push(path);
			onNavigate?.();
		},
		[history, onNavigate, onAiAssistantOpen]
	);

	const resetCreateChatForm = useCallback(() => {
		setCreateGroupOpened(false);
		setSelectedUsers([]);
		setGroupName("");
		setIsPrivateChat(false);
		setCreateChatType("group");
	}, []);

	// Include hidden and archived direct chats when filtering new contacts.
	useEffect(() => {
		if (!createGroupOpened) return undefined;
		let cancelled = false;
		getUsers();
		setChatContactsLoading(true);
		API.get({ route: config.api.chat.chatGroup, query: { chatType: "direct", users: user._id } })
			.then((chats) => {
				if (!cancelled) setExistingDirectChats(Array.isArray(chats) ? chats : []);
			})
			.catch((error) => console.error("Failed to load direct chats:", error))
			.finally(() => { if (!cancelled) setChatContactsLoading(false); });
		return () => { cancelled = true; };
	}, [createGroupOpened, getUsers, user._id]);

	// Create group chat
	const handleCreateGroup = useCallback(async () => {
		if (selectedUsers.length === 0 || creatingChatRef.current || chatContactsLoading) return;
		if (createChatType === "direct" && (selectedUsers.length !== 1 ||
			hasDirectChatWithUser([...allChats, ...existingDirectChats], user._id, selectedUsers[0]))) return;
		creatingChatRef.current = true;
		setIsCreatingChat(true);

		try {
			NProgress.start();
			const isDirectChat = createChatType === "direct";
			const resolvedChatType = isDirectChat ? "direct" : createChatType;
			const chatData = {
				users: [user._id, ...selectedUsers],
				admins: [user._id],
				privateChat: isDirectChat || isPrivateChat,
				readOnly: resolvedChatType === "channel",
				name: isDirectChat ? undefined : groupName.trim() || undefined,
				chatType: resolvedChatType,
				createdAt: new Date(),
				updated: new Date()
			};

			const newChat = await API.post(config.api.chat.chatGroup, chatData);

			if (newChat) {
				const invitedUserIds = selectedUsers
					.map((memberId) => String(memberId || ""))
					.filter(Boolean);
				const chatRoute = buildChatRoute(config.api.chat.chatGroup, newChat._id);
				const actorNames = buildLocalizedNotificationMap(
					(currentLang) =>
						getLocalizedText(user?.fullName, currentLang) ||
						getLocalizedText(user?.name, currentLang) ||
						user?.username ||
						T("nothingFound", currentLang)
				);
				const chatNames = buildLocalizedNotificationMap(
					(currentLang) =>
						getLocalizedText(groupName, currentLang) ||
						T(resolvedChatType === "direct" ? "chatDirectChat" : "chatGroupChat", currentLang)
				);
				if (!isDirectChat && invitedUserIds.length) {
					createNotification({
						date: new Date().toISOString(),
						name: buildLocalizedNotificationMap((currentLang) =>
							T("chatAddedToGroupNotification", currentLang)
						),
						desc: buildLocalizedNotificationMap(
							(currentLang) =>
								`${actorNames[currentLang]} ${T("chatAddedYouToGroup", currentLang)} ${chatNames[currentLang]}`
						),
						importance: "medium",
						route: chatRoute,
						createdByUser: user._id,
						targetUsers: invitedUserIds.map((targetUserId) => ({
							user: targetUserId,
							read: false
						}))
					});
				}
				if (invitedUserIds.length) {
					socket?.emit(
						"chatUsersAdded",
						JSON.stringify({
							chatId: newChat._id,
							route: chatRoute,
							userIds: invitedUserIds
						})
					);
				}
				resetCreateChatForm();
				getChats();
				navigateFromSidebar(chatRoute);
			}
		} catch (error) {
			console.error("Error creating group:", error);
		} finally {
			creatingChatRef.current = false;
			setIsCreatingChat(false);
			NProgress.done();
		}
	}, [
		selectedUsers,
		chatContactsLoading,
		allChats,
		existingDirectChats,
		user._id,
		user?.fullName,
		user?.name,
		user?.username,
		isPrivateChat,
		createChatType,
		groupName,
		resetCreateChatForm,
		getChats,
		navigateFromSidebar,
		createNotification,
		socket
	]);

	const startDirectChat = useCallback(
		async (targetUserId) => {
			if (!targetUserId || !user?._id) return;

			try {
				NProgress.start();
				const chat = await resolveDirectChatForUser({
					API,
					chatGroupRoute: config.api.chat.chatGroup,
					chatUserStateRoute: config.api.chat.userState,
					currentUserId: user._id,
					targetUserId
				});
				if (chat?._id) {
					setChatSearchQuery("");
					setChatUserSearchResults([]);
					await getChats();
					navigateFromSidebar(buildChatRoute(config.api.chat.chatGroup, chat._id));
				}
			} catch (error) {
				console.error("Failed to start direct chat:", error);
			} finally {
				NProgress.done();
			}
		},
		[user?._id, getChats, navigateFromSidebar]
	);

	const updateChatInSidebar = useCallback((chatId, updater) => {
		setChats((currentChats) =>
			currentChats.map((chat) =>
				String(chat?._id || "") === String(chatId || "") ? updater(chat) : chat
			)
		);
	}, []);

	const handleChatContextMenu = useCallback((event, chat) => {
		event.preventDefault();
		chatMenuPosRef.current = { x: event.clientX, y: event.clientY };
		// Right-click: open rightward from the cursor.
		setChatContextMenu({
			chat,
			x: event.clientX,
			y: event.clientY,
			position: "bottom-start"
		});
	}, []);

	// Same menu, opened from the visible arrow button — right-aligned to the
	// button's right edge so it opens leftward (from the right), not into the
	// chat area. Acts as a toggle: clicking the same chat's arrow again closes it.
	const handleChatMenuButton = useCallback((event, chat) => {
		event.preventDefault();
		event.stopPropagation();
		const rect = event.currentTarget.getBoundingClientRect();
		setChatContextMenu((prev) => {
			if (prev && String(prev.chat?._id) === String(chat?._id)) {
				return null;
			}
			chatMenuPosRef.current = { x: rect.right, y: rect.bottom };
			return { chat, x: rect.right, y: rect.bottom, position: "bottom-end" };
		});
	}, []);

	const handlePinChat = useCallback(async (chat) => {
		// Close before the list reflow so the menu never re-anchors mid-animation.
		setChatContextMenu(null);
		const nextPinned = !chat?.userState?.pinned;
		try {
			const state = await API.update(config.api.chat.userState, chat._id, {
				pinned: nextPinned
			});
			updateChatInSidebar(chat._id, (item) => ({ ...item, userState: state }));
			await getChats();
		} catch (error) {
			console.error("Failed to update pinned chat state:", error);
		}
	}, [getChats, updateChatInSidebar]);

	// durationHours: number => mute until now + N hours; "forever" => mute with no expiry; null => unmute.
	const handleMuteChat = useCallback(async (chat, durationHours = null) => {
		// Close the menu (and its submenu) immediately on selection.
		setChatContextMenu(null);
		let payload;
		if (durationHours === null) {
			payload = { muted: false, mutedUntil: null };
		} else if (durationHours === "forever") {
			payload = { muted: true, mutedUntil: null };
		} else {
			payload = {
				muted: true,
				mutedUntil: new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString()
			};
		}
		try {
			const state = await API.update(config.api.chat.userState, chat._id, payload);
			updateChatInSidebar(chat._id, (item) => ({ ...item, userState: state }));
			window.dispatchEvent(new CustomEvent("apit:chat-user-state-updated", { detail: { chatId: chat._id } }));
		} catch (error) {
			console.error("Failed to update muted chat state:", error);
		}
	}, [updateChatInSidebar]);

	const handleArchiveChat = useCallback(async (chat) => {
		// Close before the list reflow so the menu never re-anchors mid-animation.
		setChatContextMenu(null);
		const nextArchived = !chat?.userState?.archived;
		try {
			const state = await API.update(config.api.chat.userState, chat._id, {
				archived: nextArchived
			});
			updateChatInSidebar(chat._id, (item) => ({ ...item, userState: state }));
			await getChats();
		} catch (error) {
			console.error("Failed to update archived chat state:", error);
		}
	}, [getChats, updateChatInSidebar]);

	const handleMarkChatUnread = useCallback(async (chat, unread) => {
		try {
			const state = await API.update(config.api.chat.userState, chat._id, {
				manualUnread: unread
			});
			updateChatInSidebar(chat._id, (item) => ({ ...item, userState: state }));
		} catch (error) {
			console.error("Failed to update unread chat state:", error);
		} finally {
			setChatContextMenu(null);
		}
	}, [updateChatInSidebar]);

	// Opening a chat clears any manual "mark as unread" flag (optimistic + persist).
	const clearManualUnread = useCallback((chat) => {
		if (!chat?.userState?.manualUnread) return;
		updateChatInSidebar(chat._id, (item) => ({
			...item,
			userState: { ...(item.userState || {}), manualUnread: false }
		}));
		API.update(config.api.chat.userState, chat._id, { manualUnread: false }).catch((error) =>
			console.error("Failed to clear manual unread:", error)
		);
	}, [updateChatInSidebar]);

	const isChatActive = useCallback((chatId) => {
		const currentPath = location.pathname;
		if (currentPath.startsWith("/chat/chat-group/")) {
			return chatId === currentPath.split("/chat/chat-group/")[1]?.split("/")[0];
		}
		return false;
	}, [location.pathname]);

	// Shared renderer for the Pinned / Archived groups (mixed chat types).
	const renderChatNav = useCallback(
		(item, { keyPrefix = "chat", showTypeBadge = false, showLastMessage = false, itemStyles } = {}) => (
			<ChatNavLink
				key={`${keyPrefix}-${item._id}`}
				item={item}
				index={0}
				baseIndex={0}
				route="/chat/chat-group/"
				onClick={() => {
					clearManualUnread(item);
					navigateFromSidebar(`/chat/chat-group/${item._id}`);
				}}
				active={isChatActive(item._id)}
				user={user}
				lang={lang}
				connectedUsers={connectedUsers}
				showLastMessage={showLastMessage}
				showLastMessageTimestamp={showSidebarLastMessage}
				compact={sidebarCompactMode}
				onContextMenu={handleChatContextMenu}
				onOpenMenu={handleChatMenuButton}
				showTypeBadge={showTypeBadge}
				styles={itemStyles}
			/>
		),
		[
			user,
			lang,
			connectedUsers,
			showSidebarLastMessage,
			sidebarCompactMode,
			handleChatContextMenu,
			navigateFromSidebar,
			clearManualUnread,
			isChatActive
		]
	);

	const handleBlockChatUser = useCallback(async (chat) => {
		const isBlocked = (chat?.blockedUsers || []).some((blockedUserId) => String(blockedUserId) === String(user?._id));
		if (!isBlocked) {
			setChatContextMenu(null);
			const confirmResult = await Swal.fire({
				title: T("chatBlockUser", lang),
				text: T("chatBlockUserConfirm", lang),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText: T("yes", lang),
				cancelButtonText: T("cancel", lang)
			});
			if (!confirmResult.isConfirmed) return;
		}
		try {
			const updatedChat = await API.post(
				isBlocked ? config.api.chat.unblockUser(chat._id) : config.api.chat.blockUser(chat._id),
				{}
			);
			updateChatInSidebar(chat._id, (item) => ({
				...item,
				...updatedChat,
				userState: item.userState
			}));
		} catch (error) {
			console.error("Failed to update blocked user state:", error);
		} finally {
			setChatContextMenu(null);
		}
	}, [user?._id, lang, updateChatInSidebar]);

	const handleClearChatHistory = useCallback(async (chat) => {
		setChatContextMenu(null);
		const confirmResult = await Swal.fire({
			title: T("chatClearHistory", lang),
			text: T("chatClearHistoryConfirm", lang),
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#d33",
			cancelButtonColor: "#3085d6",
			confirmButtonText: T("yes", lang),
			cancelButtonText: T("cancel", lang)
		});
		if (!confirmResult.isConfirmed) return;
		try {
			await API.post(config.api.chat.clearHistory(chat._id), {});
			await getChats();
			if (location.pathname.includes(`/chat/chat-group/${chat._id}`)) {
				window.dispatchEvent(new CustomEvent("apit:chat-history-cleared", { detail: { chatId: chat._id } }));
			}
		} catch (error) {
			console.error("Failed to clear chat history:", error);
		}
	}, [getChats, lang, location.pathname]);

	const handleDeleteChatForMe = useCallback(async (chat) => {
		setChatContextMenu(null);
		const confirmResult = await Swal.fire({
			title: T("chatDeleteForMe", lang),
			text: T("chatDeleteForMeConfirm", lang),
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#d33",
			cancelButtonColor: "#3085d6",
			confirmButtonText: T("yes", lang),
			cancelButtonText: T("cancel", lang)
		});
		if (!confirmResult.isConfirmed) return;
		try {
			await API.post(config.api.chat.deleteForMe(chat._id), {});
			setChats((currentChats) => currentChats.filter((item) => String(item?._id) !== String(chat._id)));
			if (location.pathname.includes(`/chat/chat-group/${chat._id}`)) {
				navigateFromSidebar("/chat/chats");
			}
		} catch (error) {
			console.error("Failed to delete chat for me:", error);
		}
	}, [lang, location.pathname, navigateFromSidebar]);

	// Initial load
	useEffect(() => {
		if (!firstTimeLoaded) {
			setFirstTimeLoaded(true);
		}
	}, [firstTimeLoaded]);

	// Find Chat platform index for accordion
	const chatPlatformIndex = platforms?.findIndex((p) => p?.route === "chat");
	const filesPlatformIndex = platforms?.findIndex((p) => p?.route === "files");
	const isFilesRoute = useMemo(() => location.pathname.startsWith("/files"), [location.pathname]);

	// Always expand direct chats when the chat sidebar section is opened
	useEffect(() => {
		if (chatPlatformIndex >= 0 && accordionValue === String(chatPlatformIndex)) {
			setDirectMessagesOpen(true);
		}
	}, [accordionValue, chatPlatformIndex]);

	// Load unread totals even with Chat collapsed; load other chat details when opened.
	useEffect(() => {
		if (!firstTimeLoaded || !user?._id) return;

		const currentPlatform = platforms?.[activePlatform];
		const isChatAccordionOpen = accordionValue === String(chatPlatformIndex);

		if (currentPlatform?.route === "chat" || isChatRoute || isChatAccordionOpen) {
			NProgress.start();
			Promise.all([getChats(), getMeetings(), getChatSidebarSettings()]).finally(() => {
				NProgress.done();
				setBusy(false);
			});
		} else if (chatPlatformIndex >= 0) {
			void getChats();
		}

		setBusy(false);
	}, [
		location.pathname,
		firstTimeLoaded,
		platforms,
		activePlatform,
		isChatRoute,
		accordionValue,
		chatPlatformIndex,
		getChats,
		getMeetings,
		getChatSidebarSettings,
		user?._id
	]);

	useEffect(() => {
		const handleGlobalChatSettingsUpdated = (event) => {
			setShowSidebarLastMessage(isSidebarLastMessageEnabled(event?.detail));
			setSidebarCompactMode(isSidebarCompactModeEnabled(event?.detail));
		};
		window.addEventListener(CHAT_GLOBAL_SETTINGS_UPDATED_EVENT, handleGlobalChatSettingsUpdated);
		return () => {
			window.removeEventListener(CHAT_GLOBAL_SETTINGS_UPDATED_EVENT, handleGlobalChatSettingsUpdated);
		};
	}, []);

	useEffect(() => {
		if (!firstTimeLoaded || !user?._id || filesBasicsLoaded) return;
		const isFilesAccordionOpen = accordionValue === String(filesPlatformIndex);
		if (!isFilesRoute && !isFilesAccordionOpen) return;

		let cancelled = false;
		Promise.all([ApiClient.get(`${filesBase}/mounts`), ApiClient.get(`${filesBase}/providers`)])
			.then(([mountRes, providerRes]) => {
				if (cancelled) return;
				setFileMounts(mountRes?.data || []);
				setFileProviders(providerRes?.data || []);
				setFilesBasicsLoaded(true);
			})
			.catch((error) => {
				console.error("Failed to load files sidebar data:", error);
			});

		return () => {
			cancelled = true;
		};
	}, [
		accordionValue,
		filesBasicsLoaded,
		filesPlatformIndex,
		firstTimeLoaded,
		isFilesRoute,
		user?._id
	]);

	// Keep chat sidebar avatars in sync after group/channel image upload in chat settings.
	useEffect(() => {
		const handleChatAvatarUpdated = (event) => {
			const chatId = event?.detail?.chatId;
			const uploads = event?.detail?.uploads;
			if (!chatId || !Array.isArray(uploads) || !uploads.length) return;

			setChats((prev) =>
				prev.map((chat) =>
					String(chat?._id) === String(chatId) ? { ...chat, uploads } : chat
				)
			);
		};

		window.addEventListener(CHAT_AVATAR_UPDATED_EVENT, handleChatAvatarUpdated);
		return () => window.removeEventListener(CHAT_AVATAR_UPDATED_EVENT, handleChatAvatarUpdated);
	}, []);

	// Separate chats into categories (archived > pinned > by chat type)
	useEffect(() => {
		const { pinned, archived, blocked, direct, group, channel } = partitionSidebarChats(chats, user?._id);
		setPinnedChats(pinned);
		setArchivedChats(archived);
		setBlockedChats(blocked);
		setDirectChats(direct);
		setChannelChats(channel);
		setGroupChats(group);
	}, [chats, user?._id]);

	// Socket event handlers
	useEffect(() => {
		const handleNewMessageInteger = (newMessage) => {
			const isViewingThisChat = window.location.pathname.includes(newMessage.chatId);
			const senderId = newMessage.from?._id || newMessage.from;

			setChats((prev) =>
				prev.map((chat) => {
					if (String(chat._id) !== String(newMessage.chatId)) return chat;

					const updated = { ...chat, lastMessage: newMessage };

					// Increment unread only when not viewing the chat and message is from someone else
					if (!isViewingThisChat && String(senderId) !== String(user._id)) {
						updated.unreadCount = (chat.unreadCount ?? chat.unreadMessages?.length ?? 0) + 1;
					}

					return updated;
				})
			);
		};

		const handleUpdatedMessageInteger = () => {
			getChats();
		};
		const handleChatMembershipUpdated = () => {
			getChats();
		};

		socket?.on("newMessageInteger", handleNewMessageInteger);
		socket?.on("updatedMessageInteger", handleUpdatedMessageInteger);
		socket?.on("chatMembershipUpdated", handleChatMembershipUpdated);

		return () => {
			socket?.off("newMessageInteger", handleNewMessageInteger);
			socket?.off("updatedMessageInteger", handleUpdatedMessageInteger);
			socket?.off("chatMembershipUpdated", handleChatMembershipUpdated);
		};
	}, [socket, user._id, getChats]);

	// Fetch time lapse data
	useEffect(() => {
		if (!user?._id) return;

		const fetchTimeLapse = async () => {
			try {
				const userTimeLapse = await API.get({
					route: config.api.system.activityTracker,
					query: { userId: user._id }
				});
				if (userTimeLapse?.todayRecord) {
					setTimeLapse({
						activeTime: userTimeLapse.todayRecord.activeTime,
						inactiveTime: userTimeLapse.todayRecord.inactiveTime
					});
				}
			} catch (error) {
				console.log("Activity tracker fetch failed:", error);
			}
		};

		fetchTimeLapse();
		const intervalId = setInterval(fetchTimeLapse, 60000);

		return () => clearInterval(intervalId);
	}, [user?._id]);

	// Handle accordion change
	const handleAccordionChange = useCallback((value) => {
		setAccordionValue(value);
	}, []);

	// Chat list content to inject into Chat platform accordion panel
	const renderChatListContent = (itemStyles) => (
		<>
			{/* Rendered through a Portal to document.body so the fixed-position
				anchor is measured against the viewport, not a transformed
				accordion/Collapse ancestor (which offset the menu and made it
				flash at the top of the sidebar during list reflows). */}
			<Portal>
				<Menu
					opened={Boolean(chatContextMenu)}
					onClose={() => setChatContextMenu(null)}
					position={chatContextMenu?.position || "bottom-start"}
					offset={2}
					withinPortal
					transitionProps={{ duration: 0, exitDuration: 0 }}
				>
					<Menu.Target>
						<Box
							style={{
								position: "fixed",
								left: chatContextMenu?.x ?? chatMenuPosRef.current.x,
								top: chatContextMenu?.y ?? chatMenuPosRef.current.y,
								width: 1,
								height: 1,
								pointerEvents: "none"
							}}
						/>
					</Menu.Target>
				<Menu.Dropdown>
					{/* 1. Archive */}
					<Menu.Item
						icon={chatContextMenu?.chat?.userState?.archived ? <IconArchiveOff size={16} /> : <IconArchive size={16} />}
						onClick={() => handleArchiveChat(chatContextMenu.chat)}
					>
						{chatContextMenu?.chat?.userState?.archived ? T("chatUnarchiveChat", lang) : T("chatArchiveChat", lang)}
					</Menu.Item>

					{/* 2. Mute notifications (timed: 8h / 1d / 1w) */}
					{isChatMuted(chatContextMenu?.chat?.userState) ? (
						<Menu.Item
							icon={<IconBell size={16} />}
							onClick={() => handleMuteChat(chatContextMenu.chat, null)}
						>
							{T("chatUnmuteNotifications", lang)}
						</Menu.Item>
					) : (
						<Menu
							trigger="hover"
							position="right-start"
							offset={4}
							withinPortal={false}
							transitionProps={{ duration: 0, exitDuration: 0 }}
						>
							<Menu.Target>
								{/* Custom row (not a Menu.Item) so the outer Menu's
									item-hover tracking can never highlight it — it lights
									up only on its own :hover. Replicates Menu.Item styling. */}
								<UnstyledButton
									sx={(theme) => ({
										...theme.fn.fontStyles(),
										fontSize: theme.fontSizes.sm,
										width: "100%",
										textAlign: "left",
										boxSizing: "border-box",
										padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
										borderRadius: theme.fn.radius("sm"),
										color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
										display: "flex",
										alignItems: "center",
										cursor: "pointer",
										"&:hover": {
											backgroundColor:
												theme.colorScheme === "dark"
													? theme.fn.rgba(theme.colors.dark[3], 0.35)
													: theme.colors.gray[1]
										}
									})}
								>
									<IconBellOff size={16} style={{ marginRight: 10, flex: "0 0 auto" }} />
									<span style={{ flex: 1 }}>{T("chatMuteNotifications", lang)}</span>
									<IconChevronRight size={14} style={{ marginLeft: 8, flex: "0 0 auto" }} />
								</UnstyledButton>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item
									icon={<IconClock size={16} />}
									onClick={() => handleMuteChat(chatContextMenu.chat, 8)}
								>
									{T("chatMuteFor8Hours", lang)}
								</Menu.Item>
								<Menu.Item
									icon={<IconClock size={16} />}
									onClick={() => handleMuteChat(chatContextMenu.chat, 24)}
								>
									{T("chatMuteFor1Day", lang)}
								</Menu.Item>
								<Menu.Item
									icon={<IconClock size={16} />}
									onClick={() => handleMuteChat(chatContextMenu.chat, 168)}
								>
									{T("chatMuteFor1Week", lang)}
								</Menu.Item>
								<Menu.Item
									icon={<IconBellOff size={16} />}
									onClick={() => handleMuteChat(chatContextMenu.chat, "forever")}
								>
									{T("chatMuteForever", lang)}
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					)}

					{/* 3. Pin */}
					<Menu.Item
						icon={chatContextMenu?.chat?.userState?.pinned ? <IconPinnedOff size={16} /> : <IconPin size={16} />}
						onClick={() => handlePinChat(chatContextMenu.chat)}
					>
						{chatContextMenu?.chat?.userState?.pinned ? T("chatUnpinChat", lang) : T("chatPinChat", lang)}
					</Menu.Item>

					{/* 4. Mark as unread / read */}
					{chatContextMenu?.chat?.userState?.manualUnread ? (
						<Menu.Item
							icon={<IconMailOpened size={16} />}
							onClick={() => handleMarkChatUnread(chatContextMenu.chat, false)}
						>
							{T("chatMarkRead", lang)}
						</Menu.Item>
					) : (
						<Menu.Item
							icon={<IconMail size={16} />}
							onClick={() => handleMarkChatUnread(chatContextMenu.chat, true)}
						>
							{T("chatMarkUnread", lang)}
						</Menu.Item>
					)}

					{/* 5. Block (direct chats only) */}
					{chatContextMenu?.chat?.chatType === "direct" && (
						<Menu.Item
							icon={<IconBan size={16} />}
							onClick={() => handleBlockChatUser(chatContextMenu.chat)}
						>
							{(chatContextMenu?.chat?.blockedUsers || []).some((blockedUserId) => String(blockedUserId) === String(user?._id))
								? T("chatUnblockUser", lang)
								: T("chatBlockUser", lang)}
						</Menu.Item>
					)}

					{/* 6-8. Destructive actions (RBAC-gated) */}
					{(canClearHistory || canDeleteChat) && <Menu.Divider />}
					{canClearHistory && (
						<Menu.Item
							icon={<IconEraser size={16} />}
							onClick={() => handleClearChatHistory(chatContextMenu.chat)}
						>
							{T("chatClearHistory", lang)}
						</Menu.Item>
					)}
					{canDeleteChat && (
						<Menu.Item
							color="red"
							icon={<IconTrash size={16} />}
							onClick={() => handleDeleteChatForMe(chatContextMenu.chat)}
						>
							{T("chatDeleteForMe", lang)}
						</Menu.Item>
					)}
					</Menu.Dropdown>
				</Menu>
			</Portal>
			<Box p="xs">
				<Group spacing={0} wrap="nowrap">
					<TextInput
						placeholder={T("searchChat", lang)}
						value={chatSearchQuery}
						onChange={(e) => setChatSearchQuery(e.target.value)}
						icon={<IconSearch size={18} />}
						size="sm"
						style={{ flex: 1 }}
						radius="sm"
						styles={{
							input: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
						}}
						rightSection={
							hasChatSearchQuery && (
								<ActionIcon onClick={() => setChatSearchQuery("")} variant="subtle" size="sm">
									<IconX size={18} />
								</ActionIcon>
							)
						}
					/>
					<Button
						onClick={() => setCreateGroupOpened(true)}
						size="xs"
						variant="light"
						radius="sm"
						style={{
							borderTopLeftRadius: 0,
							borderBottomLeftRadius: 0,
							height: "38px"
						}}
					>
						<IconUsersPlus size={20} />
					</Button>
				</Group>
			</Box>
			{hasChatSearchQuery && (
				<>
					<NavLink label={T("searchResults", lang)} disabled />
					{filteredChats.map((item, index) => (
						<ChatNavLink
							key={`search-${item._id || index}`}
							item={item}
							index={index}
							baseIndex={0}
							route="/chat/chat-group/"
							onClick={() => {
								setChatSearchQuery("");
								clearManualUnread(item);
								navigateFromSidebar(`/chat/chat-group/${item._id}`);
							}}
							active={false}
							user={user}
							lang={lang}
							connectedUsers={connectedUsers}
							showLastMessage={false}
							showLastMessageTimestamp={showSidebarLastMessage}
							compact={sidebarCompactMode}
							onContextMenu={handleChatContextMenu}
							onOpenMenu={handleChatMenuButton}
							styles={itemStyles}
						/>
					))}
					{chatUserSearchResults.length > 0 && (
						<NavLink label={T("chatUsers", lang)} disabled />
					)}
					{chatUserSearchResults.map((result) => (
						<NavLink
							key={`user-${result._id}`}
							label={getLocalizedText(result?.name, lang) || result?.username || T("unknown", lang)}
							description={result?.username}
							icon={
								<Avatar
									size={sidebarCompactMode ? "sm" : "md"}
									radius="sm"
									src={getUserImageSrc(result, { targetWidth: sidebarCompactMode ? 32 : 40 })}
								/>
							}
							p={sidebarCompactMode ? 6 : 12}
							onClick={() => startDirectChat(result._id)}
							styles={itemStyles}
						/>
					))}
					{filteredChats.length === 0 && chatUserSearchResults.length === 0 && (
						<Text size="sm" c="dimmed" px="md" py="xs">
							{T("nothingFound", lang)}
						</Text>
					)}
					<Divider my="xs" />
				</>
			)}
			{!hasChatSearchQuery && allSidebarChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(T("chatAllSection", lang), chatUnreadCounts.all)}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: allChatsOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setAllChatsOpen((o) => !o)}
					/>
					<Collapse in={allChatsOpen}>
						{allSidebarChats.map((item) =>
							renderChatNav(item, {
								keyPrefix: "all",
								showTypeBadge: true,
								showLastMessage: showSidebarLastMessage,
								itemStyles
							})
						)}
					</Collapse>
					<Divider my="xs" />
				</>
			)}
			{!hasChatSearchQuery && pinnedChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(T("chatPinnedSection", lang), chatUnreadCounts.pinned)}
						icon={<IconPin size={16} color="#228be6" />}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: pinnedOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setPinnedOpen((o) => !o)}
					/>
					<Collapse in={pinnedOpen}>
						{pinnedChats.map((item) =>
							renderChatNav(item, {
								keyPrefix: "pinned",
								showTypeBadge: true,
								showLastMessage: showSidebarLastMessage,
								itemStyles
							})
						)}
					</Collapse>
				</>
			)}
			{!hasChatSearchQuery && directChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(T("directMessages", lang), chatUnreadCounts.direct)}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: directMessagesOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setDirectMessagesOpen((o) => !o)}
					/>
					<Collapse in={directMessagesOpen}>
						{directChats.map((item, index) => (
							<ChatNavLink
								key={groupChats.length + channelChats.length + index}
								item={item}
								index={index}
								baseIndex={groupChats.length + channelChats.length}
								route="/chat/chat-group/"
								onClick={() => {
									setActivePage(groupChats.length + channelChats.length + index);
									clearManualUnread(item);
									navigateFromSidebar(`/chat/chat-group/${item._id}`);
								}}
								active={(() => {
									const currentPath = location.pathname;
									if (currentPath.startsWith("/chat/chat-group/")) {
										const chatId = currentPath.split("/chat/chat-group/")[1]?.split("/")[0];
										return item._id === chatId;
									}
									return groupChats.length + channelChats.length + index === activePage;
								})()}
								user={user}
								lang={lang}
								connectedUsers={connectedUsers}
								showLastMessage={showSidebarLastMessage}
								showLastMessageTimestamp={showSidebarLastMessage}
								compact={sidebarCompactMode}
								onContextMenu={handleChatContextMenu}
								onOpenMenu={handleChatMenuButton}
								styles={itemStyles}
							/>
						))}
					</Collapse>
				</>
			)}
			{!hasChatSearchQuery && channelChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(T("channels", lang), chatUnreadCounts.channel)}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: channelsOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setChannelsOpen((o) => !o)}
					/>
					<Collapse in={channelsOpen}>
						{channelChats.map((item, index) => (
							<ChatNavLink
								key={index}
								item={item}
								index={index}
								baseIndex={0}
								route="/chat/chat-group/"
								onClick={() => {
									setActivePage(index);
									clearManualUnread(item);
									navigateFromSidebar(`/chat/chat-group/${item._id}`);
								}}
								active={(() => {
									const currentPath = location.pathname;
									if (currentPath.startsWith("/chat/chat-group/")) {
										const chatId = currentPath.split("/chat/chat-group/")[1]?.split("/")[0];
										return item._id === chatId;
									}
									return index === activePage;
								})()}
								user={user}
								lang={lang}
								connectedUsers={connectedUsers}
								showLastMessage={false}
								showLastMessageTimestamp={showSidebarLastMessage}
								compact={sidebarCompactMode}
								onContextMenu={handleChatContextMenu}
								onOpenMenu={handleChatMenuButton}
								styles={itemStyles}
							/>
						))}
					</Collapse>
				</>
			)}
			{!hasChatSearchQuery && groupChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(T("groupChats", lang), chatUnreadCounts.group)}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: groupChatsOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setGroupChatsOpen((o) => !o)}
					/>
					<Collapse in={groupChatsOpen}>
						{groupChats.map((item, index) => (
							<ChatNavLink
								key={channelChats.length + index}
								item={item}
								index={index}
								baseIndex={channelChats.length}
								route="/chat/"
								onClick={() => {
									setActivePage(channelChats.length + index);
									clearManualUnread(item);
									navigateFromSidebar(`/chat/chat-group/${item._id}`);
								}}
								active={(() => {
									const currentPath = location.pathname;
									if (currentPath.startsWith("/chat/chat-group/")) {
										const chatId = currentPath.split("/chat/chat-group/")[1]?.split("/")[0];
										return item._id === chatId;
									}
									return channelChats.length + index === activePage;
								})()}
								user={user}
								lang={lang}
								connectedUsers={connectedUsers}
								showLastMessage={false}
								showLastMessageTimestamp={showSidebarLastMessage}
								compact={sidebarCompactMode}
								onContextMenu={handleChatContextMenu}
								onOpenMenu={handleChatMenuButton}
								styles={itemStyles}
							/>
						))}
					</Collapse>
				</>
			)}
			{!hasChatSearchQuery && archivedChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(`${T("chatArchivedSection", lang)} (${archivedChats.length})`, chatUnreadCounts.archived)}
						icon={<IconArchive size={16} color="#868e96" />}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: archivedOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setArchivedOpen((o) => !o)}
					/>
					<Collapse in={archivedOpen}>
						{archivedByType.direct.length > 0 && (
							<>
								<NavLink label={T("directMessages", lang)} disabled />
								{archivedByType.direct.map((item) =>
									renderChatNav(item, { keyPrefix: "arch-direct", itemStyles })
								)}
							</>
						)}
						{archivedByType.channel.length > 0 && (
							<>
								<NavLink label={T("channels", lang)} disabled />
								{archivedByType.channel.map((item) =>
									renderChatNav(item, { keyPrefix: "arch-channel", itemStyles })
								)}
							</>
						)}
						{archivedByType.group.length > 0 && (
							<>
								<NavLink label={T("groupChats", lang)} disabled />
								{archivedByType.group.map((item) =>
									renderChatNav(item, { keyPrefix: "arch-group", itemStyles })
								)}
							</>
						)}
					</Collapse>
				</>
			)}
			{!hasChatSearchQuery && blockedChats.length > 0 && (
				<>
					<NavLink
						label={chatSectionLabel(`${T("chatBlockedSection", lang)} (${blockedChats.length})`, chatUnreadCounts.blocked)}
						icon={<IconBan size={16} color="#fa5252" />}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: blockedOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setBlockedOpen((o) => !o)}
					/>
					<Collapse in={blockedOpen}>
						{blockedChats.map((item) =>
							renderChatNav(item, { keyPrefix: "blocked", itemStyles })
						)}
					</Collapse>
				</>
			)}
			{!hasChatSearchQuery && sidebarMeetings.length > 0 && (
				<>
					<NavLink
						label={T("meetings", lang)}
						rightSection={
							<IconChevronDown
								size={14}
								style={{
									transform: meetingsOpen ? "rotate(0deg)" : "rotate(-90deg)",
									transition: "transform 200ms ease"
								}}
							/>
						}
						onClick={() => setMeetingsOpen((o) => !o)}
					/>
					<Collapse in={meetingsOpen}>
						{sidebarMeetings.map((meeting) => (
							<NavLink
								key={meeting._id}
								label={
									<Box style={{ minWidth: 0, flex: 1 }}>
										<Text size="sm" truncate>
											{meeting.title}
										</Text>
										<Text size="xs" c="dimmed" truncate>
											{new Date(meeting.startDate).toLocaleString()}
										</Text>
									</Box>
								}
								rightSection={
									meeting.status === "live" && (
										<Badge
											size="sm"
											color="red"
											variant="filled"
											styles={{ root: { flexShrink: 0 } }}
										>
											LIVE
										</Badge>
									)
								}
								onClick={() => navigateFromSidebar(`/meetings/${meeting._id}`)}
								variant="subtle"
								pl="xl"
								styles={itemStyles}
							/>
						))}
					</Collapse>
				</>
			)}
		</>
	);

	const renderFilesSidebarItem = ({ key, label, icon, active, onClick }) => (
		<NavLink
			key={key}
			label={label}
			icon={icon}
			active={active}
			variant="subtle"
			onClick={onClick}
			styles={{
				label: {
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis"
				}
			}}
		/>
	);

	const renderFilesSidebarContent = () => {
		const activePath = location.pathname.replace(/\/+$/, "") || "/";
		const showProviders = isAdminUser(user);

		return (
			<>
				{fileSidebarTopSections.map((entry) =>
					renderFilesSidebarItem({
						key: entry.section,
						label: entry.label,
						icon:
							entry.section === "recent" ? (
								<IconHistory size={18} />
							) : entry.section === "favorites" ? (
								<IconStar size={18} />
							) : (
								<IconFolderSymlink size={18} />
							),
						active: activePath === filesSectionUrl(entry.section),
						onClick: () => navigateFromSidebar(filesSectionUrl(entry.section))
					})
				)}

				{fileMounts.length > 0 && (
					<>
						<Divider my={6} label="Folders" labelPosition="center" />
						{fileMounts.map((mount) => {
							const targetUrl = filesBrowserUrl({ targetType: "mount", targetId: mount._id });
							return renderFilesSidebarItem({
								key: mount._id,
								label: entityName(mount, "Mount"),
								icon: <IconFolder size={18} color={filesFolderIconColor} />,
								active: activePath === targetUrl || activePath.startsWith(`${targetUrl}/`),
								onClick: () => navigateFromSidebar(targetUrl)
							});
						})}
					</>
				)}

				<Divider my={6} />
				{fileSidebarBottomSections.map((entry) =>
					renderFilesSidebarItem({
						key: entry.section,
						label: entry.label,
						icon:
							entry.section === "apit-folders" ? (
								<IconFolderOpen size={18} />
							) : entry.section === "my-files" ? (
								<IconFolder size={18} color={filesFolderIconColor} />
							) : (
								<IconTrash size={18} />
							),
						active: activePath === filesSectionUrl(entry.section),
						onClick: () => navigateFromSidebar(filesSectionUrl(entry.section))
					})
				)}

				{showProviders && fileProviders.length > 0 && (
					<>
						<Divider my={6} label="Storage Providers" labelPosition="center" />
						{fileProviders.map((provider) => {
							const targetUrl = filesBrowserUrl({ targetType: "provider", targetId: provider._id });
							return renderFilesSidebarItem({
								key: provider._id,
								label: entityName(provider, "Provider"),
								icon: <IconShield size={18} />,
								active: activePath === targetUrl || activePath.startsWith(`${targetUrl}/`),
								onClick: () => navigateFromSidebar(targetUrl)
							});
						})}
					</>
				)}
			</>
		);
	};

	// Render Main Accordion Sidebar (always used, with chat list integrated when on Chat platform)
	const renderMainSidebar = () => (
		<ScrollArea
			type="never"
			style={{ flex: 1, minHeight: 0, overflowX: "hidden" }}
			h={height ? Math.max(height - 60, 200) : undefined}
			styles={{ viewport: { "& > div": { display: "block !important", minWidth: 0 } } }}
		>
			<Accordion
				maw={400}
				classNames={classes}
				value={accordionValue}
				onChange={handleAccordionChange}
				styles={{
					root: {
						width: "100%"
					},
					control: {
						width: "100%"
					},
					panel: {
						width: "100%",
						height: "100%"
					},
					body: {
						width: "100%",
						height: "100%"
					},
					item: {
						width: "100%",
						height: "100%"
					},
					itemLabel: {
						width: "100%",
						height: "100%"
					}
				}}
			>
				{platforms?.map((platform, platformIndex) => {
					const filteredPlatformMenu = getFilteredPlatformMenu(platform);
					const isChatPlatform = platform.route === "chat";
					const isFilesPlatform = platform.route === "files";

					// Don't render platform if no menu items and not chat (chat has dynamic content)
					if (filteredPlatformMenu.length === 0 && !isChatPlatform && !isFilesPlatform) {
						return null;
					}

					// Module color from config menu: keep idle rows neutral, color only on interaction/selection.
					const moduleColor = platform.color;
					const headerControlStyle = moduleColor
						? {
								"--sidebar-platform-color": moduleColor,
								"--sidebar-platform-bg": "transparent",
								"--sidebar-platform-hover-bg": hexToRgba(moduleColor, isDark ? 0.18 : 0.13),
								"--sidebar-platform-active-bg": hexToRgba(moduleColor, isDark ? 0.22 : 0.16),
								"--sidebar-platform-active-hover-bg": hexToRgba(moduleColor, isDark ? 0.26 : 0.2),
								"--sidebar-platform-ring": hexToRgba(moduleColor, isDark ? 0.32 : 0.24)
							}
						: undefined;

					// Submenu items stay neutral until hover or active.
					const navActiveStyles = moduleColor
						? {
								root: {
									transition:
										"background-color 220ms ease, color 220ms ease, font-weight 220ms ease",
									"&:hover": {
										backgroundColor: hexToRgba(moduleColor, isDark ? 0.14 : 0.08),
										color: moduleColor
									},
									"&[data-active], &[data-active]:hover": {
										backgroundColor: hexToRgba(moduleColor, isDark ? 0.18 : 0.12),
										color: moduleColor,
										fontWeight: 600
									}
								}
							}
						: undefined;
					const groupActiveStyles = moduleColor
						? {
								root: {
									transition: "background-color 220ms ease, color 220ms ease",
									"&:hover": { backgroundColor: hexToRgba(moduleColor, isDark ? 0.1 : 0.05) },
									"&[data-active], &[data-active]:hover": {
										backgroundColor: hexToRgba(moduleColor, isDark ? 0.12 : 0.065),
										color: moduleColor,
										fontWeight: 600
									}
								}
							}
						: undefined;
					const menuBadge = (item) => {
						if (platform.route !== "office" || !item?.badgeKey) return null;
						const count = Number(officeBadgeCounts[item.badgeKey]) || 0;
						if (count <= 0) return null;
						return (
							<Badge size="sm" radius="xl" color="red" variant="filled">
								{count > 99 ? "99+" : count}
							</Badge>
						);
					};

					return (
						<Accordion.Item key={platformIndex} value={`${platformIndex}`}>
							<Accordion.Control
								icon={
									<Image
										src={`/assets/${platform.icon}`}
										alt={platform.title?.[lang]}
										width={40}
										height={40}
									/>
								}
								style={headerControlStyle}
							>
								<Group spacing="xs" noWrap position="apart">
									<Text span fw={600} style={{ color: "inherit", transition: "color 260ms ease" }}>
										{platform.title?.[lang]}
									</Text>
									{isChatPlatform && chatUnreadCounts.all > 0 && (
										<Badge size="sm" radius="xl" color="red" variant="filled">
											{chatUnreadCounts.all > 99 ? "99+" : chatUnreadCounts.all}
										</Badge>
									)}
								</Group>
							</Accordion.Control>
							<Accordion.Panel>
								{platform.route === "office" && (
									<Box px="xs" pb="sm">
										<Button
											fullWidth
											leftIcon={<IconCalendarPlus size={18} />}
											color="cyan"
											onClick={() => navigateFromSidebar("/office/bookings/reservations")}
										>
											{{ hy: "Ամրագրել սենյակ", ru: "Забронировать комнату" }[lang] || "Book a room"}
										</Button>
									</Box>
								)}
								{/* Chat platform: Dashboard link + chat list integrated */}
								{isChatPlatform ? (
									<>
										{filteredPlatformMenu.map((item, itemIndex) => (
											<NavLink
												key={itemIndex}
												label={toMenuLabel(item.name, lang)}
												onClick={() => navigateFromSidebar(`/${item.route}`)}
												icon={
													<Image
														src={`/assets/${item.icon}`}
														alt="navigation-icon"
														width={28}
														height={28}
														style={assetMenuIconStyle}
													/>
												}
												active={location.pathname === `/${item.route}`}
												variant="subtle"
												styles={navActiveStyles}
											/>
										))}
										{renderChatListContent(navActiveStyles)}
									</>
								) : isFilesPlatform ? (
									renderFilesSidebarContent()
								) : (
									<>
										{filteredPlatformMenu.map((item, itemIndex) => {
											// If item has subMenu, render nested NavLink
											if (item.subMenu) {
												const groupKey = `${platform.id || platform.route}:${item.id || itemIndex}`;
												const platformGroupPrefix = `${platform.id || platform.route}:`;
												const activeChild = item.subMenu.some((subItem) =>
													location.pathname.startsWith(`/${menuRoutePathOnly(subItem.route)}`)
												);
												return (
													<NavLink
														key={itemIndex}
														label={toMenuLabel(item.name, lang)}
														icon={
															item.iconUrl ? (
																<Image
																	src={item.iconUrl}
																	alt=""
																	width={28}
																	height={28}
																	fit="cover"
																	radius="sm"
																	style={{ flexShrink: 0, objectFit: "cover" }}
																/>
															) : (
																<Image
																	src={getMenuIconSrc(item.route, item.icon)}
																	alt="navigation-icon"
																	width={28}
																	height={28}
																	style={assetMenuIconStyle}
																/>
															)
														}
													childrenOffset={28}
													active={activeChild}
													opened={activeChild || Boolean(expandedMenuGroups[groupKey])}
													onChange={(opened) => setExpandedMenuGroups((current) => {
														const next = { ...current };
														Object.keys(next).forEach((key) => {
															if (key.startsWith(platformGroupPrefix)) next[key] = false;
														});
														next[groupKey] = opened;
														return next;
													})}
													aria-label={toMenuLabel(item.name, lang)}
													styles={groupActiveStyles}
													>
														{item.subMenu.map((subItem, subIndex) => (
															<NavLink
																key={subIndex}
																label={toMenuLabel(subItem.name, lang)}
																onClick={() => navigateFromSidebar(`/${subItem.route}`)}
																icon={
																	subItem.iconUrl ? (
																		<Image
																			src={subItem.iconUrl}
																			alt=""
																			width={28}
																			height={28}
																			fit="cover"
																			radius="sm"
																			style={{ flexShrink: 0, objectFit: "cover" }}
																		/>
																	) : (
																		<Image
																			src={getMenuIconSrc(subItem.route, subItem.icon)}
																			alt="navigation-icon"
																			width={28}
																			height={28}
																			style={assetMenuIconStyle}
																		/>
																	)
																}
															active={
																	subItem.matchSubRoutes
																		? location.pathname.startsWith(`/${menuRoutePathOnly(subItem.route)}/`)
																			|| isMenuRouteActive(location, subItem.route)
																		: isMenuRouteActive(location, subItem.route)
															}
															rightSection={menuBadge(subItem)}
															variant="subtle"
																styles={navActiveStyles}
															/>
														))}
													</NavLink>
												);
											}

											// Regular menu item without subMenu
											return (
												<NavLink
													key={itemIndex}
													label={toMenuLabel(item.name, lang)}
													onClick={() => navigateFromSidebar(`/${item.route}`)}
													icon={
														<Image
															src={getMenuIconSrc(item.route, item.icon)}
															alt="navigation-icon"
															width={28}
															height={28}
															style={assetMenuIconStyle}
														/>
													}
													rightSection={
														menuBadge(item) || (item.route === "security/alerts" &&
														securityOpenAlertsCount != null &&
														securityOpenAlertsCount > 0 ? (
															<Badge
																size="sm"
																radius="xl"
																color="red"
																variant="filled"
																styles={{ root: { flexShrink: 0 } }}
															>
																{securityOpenAlertsCount > 99 ? "99+" : securityOpenAlertsCount}
															</Badge>
														) : null)
													}
													active={isMenuRouteActive(location, item.route)}
													variant="subtle"
													styles={navActiveStyles}
												/>
											);
										})}
									</>
								)}
							</Accordion.Panel>
						</Accordion.Item>
					);
				})}
			</Accordion>
		</ScrollArea>
	);

	return (
		<div
			className={`sidebar${isDark ? ` ${classes.sidebarDark}` : ""}`}
			style={{ padding: 0, height: "100%" }}
		>
			{/* Create Group Chat Modal */}
			<Modal
				opened={createGroupOpened}
				onClose={resetCreateChatForm}
				title={T("createChat", lang)}
				size="md"
			>
				<Stack spacing="md">
					<Select
						label={T("chatType", lang)}
						value={createChatType === "direct" ? "direct" : "group"}
						onChange={(value) => {
							setCreateChatType(value || "group");
							setSelectedUsers([]);
							setGroupName("");
							setIsPrivateChat(false);
						}}
						data={[
							{ value: "direct", label: T("chatDirectChat", lang) },
							{ value: "group", label: T("groupChat", lang) }
						]}
					/>
					{createChatType !== "direct" && <>
						<TextInput
							label={T("groupName", lang)}
							placeholder={T("enterGroupName", lang)}
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
						/>

						<div>
							<Select
								label={T("chatType", lang)}
								value={createChatType}
								onChange={(value) => setCreateChatType(value || "group")}
								data={[
									{ value: "group", label: T("groupChat", lang) },
									{ value: "channel", label: T("channel", lang) }
								]}
								allowDeselect={false}
							/>
							<Text size="xs" color="dimmed" mt={6}>
								{T(
									createChatType === "channel"
										? "channelWritePermissionDescription"
										: "groupChatWritePermissionDescription",
									lang
								)}
							</Text>
						</div>

					</>}

					<MultiSelect
						label={T("selectUsers", lang)}
						placeholder={T("searchUsers", lang)}
						data={createChatUserOptions}
						value={selectedUsers}
						onChange={(values) => setSelectedUsers(createChatType === "direct" ? values.slice(-1) : values)}
						maxSelectedValues={createChatType === "direct" ? 1 : undefined}
						disabled={chatContactsLoading || isCreatingChat}
						searchable
						clearable
					/>

					{selectedUsers.length > 0 && (
						<Paper p="sm" withBorder>
							<Text size="sm" weight={600} mb="xs">
								{T("selectedUsers", lang)} ({selectedUsers.length})
							</Text>
							<Group spacing="xs">
								{selectedUsers.map((userId) => {
									return (
										<Badge
											key={userId}
											rightSection={
												<ActionIcon
													size="xs"
													color="blue"
													radius="xl"
													variant="transparent"
													onClick={() =>
														setSelectedUsers(selectedUsers.filter((id) => id !== userId))
													}
												>
													<IconX size={12} />
												</ActionIcon>
											}
										>
											{createChatUserLabelsById.get(String(userId)) ||
												T("unknown", lang)}
										</Badge>
									);
								})}
							</Group>
						</Paper>
					)}

					{createChatType !== "direct" && <Switch
						label={T("privateChat", lang)}
						description={T("privateChatDescription", lang)}
						checked={isPrivateChat}
						onChange={(e) => setIsPrivateChat(e.currentTarget.checked)}
					/>}

					<Group position="flex-end" mt="md">
						<Button variant="subtle" onClick={resetCreateChatForm}>
							{T("cancel", lang)}
						</Button>
						<Button onClick={handleCreateGroup} loading={isCreatingChat} disabled={selectedUsers.length === 0 || chatContactsLoading}>
							{T(createChatType === "direct" ? "createChat" : "createGroupSubmit", lang)}
						</Button>
					</Group>
				</Stack>
			</Modal>

			<Box
				p={0}
				h="100%"
				sx={{
					background:
						theme.colorScheme === "dark"
							? "linear-gradient(165deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.02) 100%)"
							: "linear-gradient(165deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.72) 45%, rgba(255,255,255,0.62) 100%)",
					backdropFilter: "blur(14px)",
					WebkitBackdropFilter: "blur(14px)",
					boxShadow:
						theme.colorScheme === "dark"
							? "0 12px 32px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.14)"
							: "0 10px 28px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
					overflow: "hidden"
				}}
			>
				{renderMainSidebar()}
			</Box>
		</div>
	);
}
